/**
 * components/admin/DiscountsAdminCard.jsx · v2.7.58+E39
 * ═══════════════════════════════════════════════════════════════════════
 * Admin CRUD for the driver-meter discount catalogue. Every row surfaces
 * on the driver's live-meter "Apply Discount" dropdown when `active=true`.
 *
 *   · Name           — free-text label ("Senior 60+", "AAA Member $3")
 *   · Kind           — "percent" or "fixed"
 *   · Value          — 10.0 (%) or 3.00 ($)
 *   · Active         — only active rows appear on the meter
 *   · Sort Order     — ascending → order in the dropdown
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { TicketPercent, Plus, Trash2, Save, RefreshCw, Loader2, X, Edit3 } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";

const EMPTY = { name: "", kind: "percent", value: 0, active: true, sort_order: 0, notes: "" };

export default function DiscountsAdminCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoad] = useState(true);
  const [openForm, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const r = await adminApi.get("/admin/discounts");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load discounts");
    } finally { setLoad(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, sort_order: items.length + 1 }); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...EMPTY, ...row }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    if (form.kind === "percent" && form.value > 100) { toast.error("Percentage cannot exceed 100"); return; }
    setBusy(true);
    try {
      const payload = {
        name:       form.name.trim(),
        kind:       form.kind,
        value:      parseFloat(form.value) || 0,
        active:     !!form.active,
        sort_order: parseInt(form.sort_order, 10) || 0,
        notes:      form.notes?.trim() || null,
      };
      if (editing) await adminApi.put(`/admin/discounts/${editing.id}`, payload);
      else         await adminApi.post("/admin/discounts", payload);
      toast.success(editing ? "Discount updated" : "Discount created");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const toggleActive = async (row, active) => {
    setItems((prev) => prev.map((r) => r.id === row.id ? { ...r, active } : r));
    try {
      await adminApi.put(`/admin/discounts/${row.id}`, { active });
    } catch (e) {
      toast.error("Toggle failed"); load();
    }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete "${row.name}"? Drivers with this discount already applied to a trip will keep the historical memo.`)) return;
    try {
      await adminApi.delete(`/admin/discounts/${row.id}`);
      toast.success("Discount deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4 space-y-3" data-testid="discounts-admin-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-amber-400/15 border border-amber-400/40 flex items-center justify-center">
            <TicketPercent className="h-5 w-5 text-amber-300"/>
          </div>
          <div>
            <div className="text-base font-bold text-zinc-100">Discounts</div>
            <div className="text-[11px] text-zinc-500">Coupon deductions surfaced on the driver&apos;s live meter. Active rows appear in the &quot;Apply Discount&quot; dropdown.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="outline" size="sm" className="bg-zinc-950 border-zinc-700 text-xs" data-testid="discounts-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1"/>Refresh
          </Button>
          <Button onClick={openNew} size="sm" className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold" data-testid="discounts-add-btn">
            <Plus className="h-4 w-4 mr-1"/>Add Discount
          </Button>
        </div>
      </div>

      {loading && (
        <div className="py-4 text-center text-zinc-500 text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-1"/>Loading…</div>
      )}
      {!loading && items.length === 0 && (
        <div className="py-6 text-center text-zinc-500 text-xs" data-testid="discounts-empty">
          No discounts configured yet. Click <span className="text-amber-300 font-bold">Add Discount</span> to create the first one.
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="discounts-table">
            <thead className="bg-zinc-950 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">Active</th>
                <th className="text-left px-3 py-2">Order</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Kind</th>
                <th className="text-right px-3 py-2">Value</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-zinc-800" data-testid={`discount-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <Switch checked={!!r.active} onCheckedChange={(v) => toggleActive(r, v)} data-testid={`discount-toggle-${r.id}`}/>
                  </td>
                  <td className="px-3 py-2 text-zinc-400 font-mono">{r.sort_order}</td>
                  <td className="px-3 py-2 text-zinc-100 font-bold">{r.name}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${
                      r.kind === "percent" ? "bg-sky-400 text-zinc-950" : "bg-emerald-400 text-zinc-950"
                    }`}>{r.kind}</span>
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-amber-300 meter-display">
                    {r.kind === "percent" ? `${r.value}%` : `$${Number(r.value).toFixed(2)}`}
                  </td>
                  <td className="px-3 py-2 text-right space-x-1">
                    <Button onClick={() => openEdit(r)} size="sm" variant="outline" className="bg-zinc-950 border-zinc-700 h-7 px-2 text-[10px]" data-testid={`discount-edit-${r.id}`}>
                      <Edit3 className="h-3 w-3 mr-1"/>Edit
                    </Button>
                    <Button onClick={() => del(r)} size="sm" variant="outline" className="bg-zinc-950 border-rose-700 text-rose-300 h-7 px-2 text-[10px]" data-testid={`discount-delete-${r.id}`}>
                      <Trash2 className="h-3 w-3"/>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openForm && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="discount-form-overlay"
        >
          <Card
            className="bg-zinc-900 border-zinc-700 p-5 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            data-testid="discount-form-modal"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-base font-bold text-zinc-100">{editing ? "Edit Discount" : "New Discount"}</div>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-200"><X className="h-4 w-4"/></button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Senior 60+" className="bg-zinc-950 border-zinc-700 mt-1" data-testid="discount-form-name"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Kind</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-700 mt-1" data-testid="discount-form-kind"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Percentage (%)</SelectItem>
                      <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                    Value {form.kind === "percent" ? "(%)" : "($)"}
                  </Label>
                  <Input type="number" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="bg-zinc-950 border-zinc-700 mt-1 meter-display" data-testid="discount-form-value"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Sort Order</Label>
                  <Input type="number" step="1" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="bg-zinc-950 border-zinc-700 mt-1" data-testid="discount-form-sort"/>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="discount-form-active"/>
                  <span className={`text-xs font-bold uppercase tracking-widest ${form.active ? "text-emerald-300" : "text-zinc-500"}`}>
                    {form.active ? "Active" : "Disabled"}
                  </span>
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Notes (optional)</Label>
                <Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="internal admin memo" className="bg-zinc-950 border-zinc-700 mt-1" data-testid="discount-form-notes"/>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} className="bg-zinc-950 border-zinc-700" data-testid="discount-form-cancel">Cancel</Button>
              <Button onClick={save} disabled={busy} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold" data-testid="discount-form-save">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-1"/>Saving…</> : <><Save className="h-4 w-4 mr-1"/>Save</>}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </Card>
  );
}
